import { ISubmissionAnalyticsPayload } from 'wisestep-api';

export const getLocalISODateTime = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  const offset = -date.getTimezoneOffset();
  const sign = offset >= 0 ? '+' : '-';
  const offsetHours = String(Math.floor(Math.abs(offset) / 60)).padStart(2, '0');
  const offsetMinutes = String(Math.abs(offset) % 60).padStart(2, '0');
  const timeZoneOffset = `${sign}${offsetHours}:${offsetMinutes}`;

  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${timeZoneOffset}`;
};

export const getDateRangeByTimePeriod = (timePeriodId: string, customFromDate?: Date) => {
  let startTime = new Date();
  let endTime = new Date();

  switch (timePeriodId) {
    case '1H':
      startTime.setHours(startTime.getHours() - 1);
      break;
    case '8H':
      startTime.setHours(startTime.getHours() - 8);
      break;
    case 'TODAY':
      startTime.setHours(0, 0, 0, 0);
      break;
    case '24H':
      startTime.setHours(startTime.getHours() - 24);
      break;
    case 'THIS_WEEK': {
      const dayOfWeek = startTime.getDay();
      const diffToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
      startTime.setDate(startTime.getDate() - diffToMonday);
      startTime.setHours(0, 0, 0, 0);
      break;
    }
    case '7D':
      startTime.setHours(startTime.getHours() - 7 * 24);
      break;
    case '30D':
      startTime.setHours(startTime.getHours() - 30 * 24);
      break;
    case '365D':
      startTime.setHours(startTime.getHours() - 365 * 24);
      break;
    case 'LAST_QUARTER': {
      const currentMonth = endTime.getMonth() + 1;
      const currentYear = endTime.getFullYear();
      let startMonth, startYear, endMonth, endYear;
      if (currentMonth <= 3) {
        startMonth = 9;
        startYear = currentYear - 1;
        endMonth = 12;
        endYear = currentYear - 1;
      } else if (currentMonth <= 6) {
        startMonth = 0;
        startYear = currentYear;
        endMonth = 3;
        endYear = currentYear;
      } else if (currentMonth <= 9) {
        startMonth = 3;
        startYear = currentYear;
        endMonth = 6;
        endYear = currentYear;
      } else {
        startMonth = 6;
        startYear = currentYear;
        endMonth = 9;
        endYear = currentYear;
      }
      startTime = new Date(startYear, startMonth, 1);
      endTime = new Date(endYear, endMonth, 0, 23, 59, 59, 999);
      break;
    }
    case 'LAST_YEAR':
      startTime.setFullYear(startTime.getFullYear() - 1, 0, 1);
      startTime.setHours(0, 0, 0, 0);
      endTime.setFullYear(endTime.getFullYear() - 1, 11, 31);
      endTime.setHours(23, 59, 59, 999);
      break;
    case 'MONTH_TILL_DATE':
      startTime.setDate(1);
      startTime.setHours(0, 0, 0, 0);
      break;
    case 'CURRENT_QUARTER': {
      let month = startTime.getMonth();
      if (month >= 0 && month <= 2) {
        month = 0;
      } else if (month >= 3 && month <= 5) {
        month = 3;
      } else if (month >= 6 && month <= 8) {
        month = 6;
      } else {
        month = 9;
      }
      startTime.setMonth(month, 1);
      startTime.setHours(0, 0, 0, 0);
      break;
    }
    case 'YEAR_TILL_DATE':
      startTime.setMonth(0, 1);
      startTime.setHours(0, 0, 0, 0);
      break;
    case 'CUSTOM_DATE_TILL_DATE':
      startTime = new Date(customFromDate ?? new Date());
      break;
    default:
      startTime.setHours(0, 0, 0, 0);
      break;
  }

  return {
    fromDateTime: getLocalISODateTime(startTime),
    toDateTime: getLocalISODateTime(endTime),
  };
};

export const getDateRangeByTimePeriodForTTS = (
  timePeriodId: string,
  query?: ISubmissionAnalyticsPayload
) => {
  let startTime = new Date();
  let endTime = new Date();
  switch (timePeriodId) {
    case 'Less than 1 hour':
      startTime = new Date(startTime.getTime() - 1 * 60 * 60 * 1000);
      break;
    case '1-3 hours':
      startTime = new Date(startTime.getTime() - 3 * 60 * 60 * 1000);
      endTime = new Date(endTime.getTime() - 1 * 60 * 60 * 1000);
      break;
    case '3-8 hours':
      startTime = new Date(startTime.getTime() - 8 * 60 * 60 * 1000);
      endTime = new Date(endTime.getTime() - 3 * 60 * 60 * 1000);
      break;
    case '8-24 hours':
      startTime = new Date(startTime.getTime() - 24 * 60 * 60 * 1000);
      endTime = new Date(endTime.getTime() - 8 * 60 * 60 * 1000);
      break;
    case '24 hours to 1 week':
      startTime = new Date(startTime.getTime() - 7 * 24 * 60 * 60 * 1000);
      endTime = new Date(endTime.getTime() - 24 * 60 * 60 * 1000);
      break;
    case '1 week to 1 Month':
      startTime = new Date(startTime.getTime() - 30 * 24 * 60 * 60 * 1000);
      endTime = new Date(endTime.getTime() - 7 * 24 * 60 * 60 * 1000);
      break;
    case '1 Month - till date':
      if (query?.range === 'LAST_YEAR') {
        const { fromDateTime, toDateTime } = getDateRangeByTimePeriod('LAST_YEAR');
        return { fromDateTime, toDateTime };
      } else if (query?.range === 'LAST_QUARTER') {
        const { fromDateTime, toDateTime } = getDateRangeByTimePeriod('LAST_QUARTER');
        return { fromDateTime, toDateTime };
      } else if (query?.range === 'CURRENT_QUARTER') {
        const { fromDateTime } = getDateRangeByTimePeriod('CURRENT_QUARTER');
        return {
          fromDateTime,
          toDateTime: getLocalISODateTime(new Date(endTime.getTime() - 30 * 24 * 60 * 60 * 1000)),
        };
      } else if (query?.range === 'YEAR_TILL_DATE') {
        const { fromDateTime } = getDateRangeByTimePeriod('YEAR_TILL_DATE');
        return {
          fromDateTime,
          toDateTime: getLocalISODateTime(new Date(endTime.getTime() - 30 * 24 * 60 * 60 * 1000)),
        };
      }
      break;
    default:
      startTime.setHours(0, 0, 0, 0);
      break;
  }

  return {
    fromDateTime: getLocalISODateTime(startTime),
    toDateTime: getLocalISODateTime(endTime),
  };
};

export const getDateRangeByFrequency = (dateTime: string | Date, frequncy: string) => {
  const startTime = new Date(dateTime);
  const endTime = new Date(dateTime);

  switch (frequncy) {
    case 'HOURLY':
      startTime.setMinutes(0, 0, 0);
      endTime.setMinutes(59, 59, 999);
      break;
    case 'WEEKLY': {
      const dayOfWeek = startTime.getDay();
      const diffToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
      startTime.setDate(startTime.getDate() - diffToMonday);
      startTime.setHours(0, 0, 0, 0);
      endTime.setDate(startTime.getDate() + 6);
      endTime.setHours(23, 59, 59, 999);
      break;
    }
    case 'MONTHLY':
      startTime.setDate(1);
      startTime.setHours(0, 0, 0, 0);
      endTime.setMonth(endTime.getMonth() + 1, 1);
      endTime.setHours(23, 59, 59, 999);
      endTime.setHours(endTime.getHours() - 24);
      break;
    default:
      startTime.setHours(0, 0, 0, 0);
      endTime.setHours(23, 59, 59, 999);
  }

  return {
    fromDateTime: getLocalISODateTime(startTime),
    toDateTime: getLocalISODateTime(endTime),
  };
};
